/**
 * @file src/lib/queryHelpers.js
 * @description Shared helpers for React Query hooks.
 *
 * Rules:
 *  - No React, no hooks here — these are query-fn wrappers / cache helpers
 *  - Only generic shared patterns belong here
 */

import { ApiError } from "lib/axios";
import { QUERY_KEYS } from "config/queryKeys";

/**
 * Wrap a list-fetching API call so a 404 from the backend is treated
 * as "no records yet" rather than a hard error.
 *
 * Use ONLY for collection endpoints (lists), never for single-resource
 * reads or mutations:
 *
 *   queryFn: gracefulList(getVendors, { vendors: [] })
 *
 * @param {Function} apiFn  - The API function to call
 * @param {object}   empty  - Shape to return on 404 (e.g. { vendors: [] })
 * @returns {Function} A wrapped async function suitable for queryFn
 */
export const gracefulList =
  (apiFn, empty) =>
  async (...args) => {
    try {
      return await apiFn(...args);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return empty;
      throw err;
    }
  };

/**
 * Invalidate all data that depends on the currently selected company.
 * Call after /companies/select succeeds so stale company-scoped data
 * from the previous selected company is not reused.
 *
 * @param {import("@tanstack/react-query").QueryClient} queryClient
 */
export const invalidateCompanyScopedQueries = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.auth.me() });
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companies.active() });
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.company.all });
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.clients.all });
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.products.all });
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payments.all });
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.devices.all });
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all });
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.branches.all });
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.auditLogs.all });
};
